import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class VacationAvailableDaysParams {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class VacationScheduleMyDeadlineParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class VacationScheduleFavoritesParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class VacationScheduleStartParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class UpdateVacationScheduleParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class DeleteVacationScheduleParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class GetVacationScheduleParams {
  @IsUUID()
  @IsNotEmpty()
  schedule_id: string;
}

export class GetVacationCustomTypesQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsInt()
  @IsNotEmpty()
  year: number;
}

export class SearchVacationSchedulesQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsOptional()
  q?: string;
}

export type UnitType = string;

export class UnitItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsNotEmpty()
  type: 'operational' | 'legal';
}

export class VacationAvailableDaysPeriod {
  @IsDateString()
  @IsNotEmpty()
  period_start: string;

  @IsDateString()
  @IsNotEmpty()
  period_end: string;

  @IsNumber()
  @IsNotEmpty()
  days: number;
}

export class VacationAvailableDays {
  @IsUUID()
  @IsNotEmpty()
  vacation_type: string;

  @IsString()
  @IsNotEmpty()
  type_name: string;

  @IsNumber()
  @IsNotEmpty()
  available_days: number;

  @IsBoolean()
  @IsOptional()
  allow_compensatory?: boolean;

  @ValidateNested({ each: true })
  @Type(() => VacationAvailableDaysPeriod)
  @IsArray()
  @IsOptional()
  periods?: VacationAvailableDaysPeriod[];
}

export class VacationAvailableDaysResponse {
  @ValidateNested({ each: true })
  @Type(() => VacationAvailableDays)
  @IsArray()
  @IsNotEmpty()
  available_days_by_types: VacationAvailableDays[];

  @ValidateNested({ each: true })
  @Type(() => UnitItem)
  @IsArray()
  @IsNotEmpty()
  units: UnitItem[];
}

export class VacationScheduleMyDeadlineResponse {
  @IsUUID()
  @IsOptional()
  schedule_id?: string;

  @IsDateString()
  @IsOptional()
  deadline?: string;
}

export class VacationFavoritesResponse {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  event_ids: string[];

  @IsInt()
  @IsNotEmpty()
  total: number;

  @IsInt()
  @IsNotEmpty()
  max_allowed: number;
}

export type EmployeeCustomType = string;

export type VacationScheduleType = string;

export class VacationExcludedEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsOptional()
  middle_name?: string;

  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsDateString()
  @IsOptional()
  dismissed_at?: string;
}

export class VacationScheduleStart_options {
  @IsDateString()
  @IsOptional()
  include_employee_hired_before?: string;

  @IsDateString()
  @IsOptional()
  exclude_disabled_contract_until?: string;

  @IsDateString()
  @IsOptional()
  exclude_unpayed_vacation_until?: string;

  @IsDateString()
  @IsOptional()
  exclude_maternity_leave_until?: string;

  @ValidateNested({ each: true })
  @Type(() => VacationExcludedEmployee)
  @IsArray()
  @IsNotEmpty()
  exclude_employees: VacationExcludedEmployee[];
}

export class VacationAssistantApprover {
  @IsIn(['operational', 'direct'])
  @IsString()
  @IsNotEmpty()
  type: 'operational' | 'direct';

  @IsInt()
  @IsOptional()
  approve_levels?: number;
}

export class VacationGroupApprover {
  @IsString()
  @IsNotEmpty()
  group_name: string;
}

export class VacationManagerApprover {
  @IsIn(['legal', 'operational', 'functional', 'direct'])
  @IsString()
  @IsNotEmpty()
  type: 'legal' | 'operational' | 'functional' | 'direct';

  @IsInt()
  @IsOptional()
  approve_levels?: number;
}

export class VacationEntityApprover {
  @IsDateString()
  @IsNotEmpty()
  deadline: string;

  @IsBoolean()
  @IsNotEmpty()
  can_edit: boolean;

  @ValidateNested()
  @Type(() => VacationManagerApprover)
  @IsOptional()
  manager_approve?: VacationManagerApprover;

  @ValidateNested()
  @Type(() => VacationGroupApprover)
  @IsOptional()
  group_approve?: VacationGroupApprover;

  @ValidateNested()
  @Type(() => VacationAssistantApprover)
  @IsOptional()
  assistant_approve?: VacationAssistantApprover;
}

export class MinWeekendDaysValidator {
  @IsInt()
  @IsNotEmpty()
  working_days: number;

  @IsInt()
  @IsNotEmpty()
  min_weekend_days: number;
}

export class DurationValidator {
  @IsInt()
  @IsNotEmpty()
  days: number;

  @IsIn(['calendar', 'work'])
  @IsString()
  @IsNotEmpty()
  unit: 'calendar' | 'work';
}

export class VacationValidators {
  @IsBoolean()
  @IsNotEmpty()
  must_schedule_all_days: boolean;

  @IsBoolean()
  @IsNotEmpty()
  must_schedule_14days: boolean;

  @IsInt()
  @IsOptional()
  days_must_be_divisible_by?: number;

  @ValidateNested()
  @Type(() => DurationValidator)
  @IsOptional()
  min_duration?: DurationValidator;

  @ValidateNested()
  @Type(() => DurationValidator)
  @IsOptional()
  max_duration?: DurationValidator;

  @IsBoolean()
  @IsNotEmpty()
  limit_to_calculated_days: boolean;

  @IsBoolean()
  @IsNotEmpty()
  min_5_days_between_not_workdays: boolean;

  @IsBoolean()
  @IsOptional()
  weekend_exclusion?: boolean;

  @IsBoolean()
  @IsOptional()
  holydays_exclusion?: boolean;

  @IsBoolean()
  @IsOptional()
  min_specific_weekend_days?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  working_day: boolean;

  @ValidateNested()
  @Type(() => MinWeekendDaysValidator)
  @IsOptional()
  min_weekend_days?: MinWeekendDaysValidator;
}

export class VacationScheduleEvent_stats {
  @IsInt()
  @IsNotEmpty()
  total: number;

  @IsInt()
  @IsNotEmpty()
  new: number;

  @IsInt()
  @IsNotEmpty()
  on_approve: number;

  @IsInt()
  @IsNotEmpty()
  send_to_1c: number;

  @IsInt()
  @IsNotEmpty()
  canceled: number;

  @IsInt()
  @IsNotEmpty()
  completed: number;
}

export class VacationSchedule {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['new', 'starting', 'active', 'completed', 'canceled'])
  @IsString()
  @IsNotEmpty()
  status: 'new' | 'starting' | 'active' | 'completed' | 'canceled';

  @ValidateNested()
  @Type(() => VacationScheduleEvent_stats)
  @IsNotEmpty()
  event_stats: VacationScheduleEvent_stats;

  @IsInt()
  @IsNotEmpty()
  year: number;

  @IsDateString()
  @IsNotEmpty()
  active_to: string;

  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsBoolean()
  @IsNotEmpty()
  send_to_1c: boolean;

  @IsString()
  @IsOptional()
  company_comment?: string;

  @IsString()
  @IsOptional()
  base_name?: string;

  @IsString()
  @IsOptional()
  custom_name?: string;

  @IsString()
  @IsOptional()
  full_name?: string;

  @IsBoolean()
  @IsNotEmpty()
  allow_employee_comment: boolean;

  @IsBoolean()
  @IsNotEmpty()
  add_previous_vacation: boolean;

  @IsBoolean()
  @IsNotEmpty()
  skip_missing_managers: boolean;

  @ValidateNested()
  @Type(() => VacationValidators)
  @IsNotEmpty()
  validators: VacationValidators;

  @IsNotEmpty()
  validator_vacation_types: object;

  @IsDateString()
  @IsNotEmpty()
  employee_deadline: string;

  @ValidateNested({ each: true })
  @Type(() => VacationEntityApprover)
  @IsArray()
  @IsNotEmpty()
  approvers: VacationEntityApprover[];

  @IsIn(['unit', 'all'])
  @IsString()
  @IsOptional()
  teammate_access?: 'unit' | 'all';

  @ValidateNested()
  @Type(() => VacationScheduleStart_options)
  @IsNotEmpty()
  start_options: VacationScheduleStart_options;

  @IsDateString()
  @IsNotEmpty()
  created_at: string;

  @ValidateNested({ each: true })
  @Type(() => UnitItem)
  @IsArray()
  @IsOptional()
  units?: UnitItem[];

  @IsIn(['company', 'units', 'custom_type'])
  @IsString()
  @IsNotEmpty()
  type: 'company' | 'units' | 'custom_type';

  @IsString()
  @IsOptional()
  custom_type?: string;

  @IsBoolean()
  @IsNotEmpty()
  available_adding_employees: boolean;

  @IsBoolean()
  @IsNotEmpty()
  unit_planned_vacations_percent_enabled: boolean;

  @IsInt()
  @IsOptional()
  threshold_percent?: number;
}

export class CreateVacationScheduleResponse {
  @IsInt()
  @IsNotEmpty()
  success: number;

  @IsInt()
  @IsNotEmpty()
  errors: number;

  @ValidateNested({ each: true })
  @Type(() => VacationSchedule)
  @IsArray()
  @IsNotEmpty()
  items: VacationSchedule[];
}

export class VacationCustomTypeItem {
  @IsString()
  @IsNotEmpty()
  custom_type: string;

  @IsBoolean()
  @IsNotEmpty()
  has_vacation_schedule: boolean;
}

export class GetVacationCustomTypesResponse {
  @ValidateNested({ each: true })
  @Type(() => VacationCustomTypeItem)
  @IsArray()
  @IsNotEmpty()
  items: VacationCustomTypeItem[];

  @IsIn(['company', 'units', 'custom_type'], { each: true })
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  available_types: ('company' | 'units' | 'custom_type')[];
}

export class UnitItemShort {
  @IsString()
  @IsNotEmpty()
  name: string;
}

export class CompanyVacationSchedule {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsInt()
  @IsNotEmpty()
  year: number;

  @IsIn(['new', 'starting', 'active', 'completed', 'canceled'])
  @IsString()
  @IsNotEmpty()
  status: 'new' | 'starting' | 'active' | 'completed' | 'canceled';

  @IsBoolean()
  @IsNotEmpty()
  favorites_allowed: boolean;

  @IsBoolean()
  @IsNotEmpty()
  percent_report_available: boolean;

  @ValidateNested({ each: true })
  @Type(() => UnitItemShort)
  @IsArray()
  @IsOptional()
  units?: UnitItemShort[];

  @IsIn(['company', 'units', 'custom_type'])
  @IsString()
  @IsNotEmpty()
  type: 'company' | 'units' | 'custom_type';

  @IsString()
  @IsOptional()
  custom_type?: string;

  @IsString()
  @IsOptional()
  base_name?: string;

  @IsString()
  @IsOptional()
  custom_name?: string;

  @IsString()
  @IsOptional()
  full_name?: string;
}

export class CompanyVacationSchedulesListItem {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsBoolean()
  @IsOptional()
  has_actions?: boolean;

  @ValidateNested({ each: true })
  @Type(() => CompanyVacationSchedule)
  @IsArray()
  @IsNotEmpty()
  vacation_schedules: CompanyVacationSchedule[];
}

export class VacationScheduleListResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyVacationSchedulesListItem)
  @IsArray()
  @IsNotEmpty()
  companies: CompanyVacationSchedulesListItem[];
}

export class SearchVacationScheduleItem {
  @IsString()
  @IsNotEmpty()
  full_name: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  external_ids: string[];
}

export class SearchVacationSchedulesResponse {
  @ValidateNested({ each: true })
  @Type(() => SearchVacationScheduleItem)
  @IsArray()
  @IsNotEmpty()
  items: SearchVacationScheduleItem[];
}

export class AdminVacationSchedulesListItem {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsNotEmpty()
  company_name: string;

  @IsBoolean()
  @IsOptional()
  has_actions?: boolean;

  @IsBoolean()
  @IsOptional()
  has_schedule_for_next_year?: boolean;

  @ValidateNested({ each: true })
  @Type(() => VacationSchedule)
  @IsArray()
  @IsNotEmpty()
  vacation_schedules: VacationSchedule[];
}

export class VacationScheduleAdminListResponse {
  @ValidateNested({ each: true })
  @Type(() => AdminVacationSchedulesListItem)
  @IsArray()
  @IsNotEmpty()
  companies: AdminVacationSchedulesListItem[];
}

export class VacationAdminCompany {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  has_schedule_for_next_year: boolean;

  @IsBoolean()
  @IsNotEmpty()
  can_create_company_type_schedule: boolean;

  @IsBoolean()
  @IsNotEmpty()
  unit_planned_vacations_percent_enabled: boolean;
}

export class VacationAdminCompaniesListResponse {
  @ValidateNested({ each: true })
  @Type(() => VacationAdminCompany)
  @IsArray()
  @IsNotEmpty()
  companies: VacationAdminCompany[];
}

export class VacationFavoritesRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  event_ids: string[];
}

export class StartVacationScheduleRequest {
  @IsDateString()
  @IsOptional()
  include_employee_hired_before?: string;

  @IsDateString()
  @IsOptional()
  exclude_disabled_contract_until?: string;

  @IsDateString()
  @IsOptional()
  exclude_unpayed_vacation_until?: string;

  @IsDateString()
  @IsOptional()
  exclude_maternity_leave_until?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  exclude_employees?: string[];
}

export class VacationUnitItem {
  @IsUUID()
  @IsNotEmpty()
  unit_id: string;

  @IsBoolean()
  @IsNotEmpty()
  include_children_units: boolean;
}

export class UpdateVacationScheduleRequest {
  @IsBoolean()
  @IsOptional()
  include_all_new_employees?: boolean;

  @ValidateNested({ each: true })
  @Type(() => VacationUnitItem)
  @IsArray()
  @IsOptional()
  include_units?: VacationUnitItem[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  include_positions?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  include_employees?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  exclude_employees?: string[];

  @IsDateString()
  @IsOptional()
  employee_deadline?: string;

  @IsDateString()
  @IsOptional()
  active_to?: string;

  @ValidateNested({ each: true })
  @Type(() => VacationEntityApprover)
  @IsArray()
  @IsOptional()
  approver_deadlines?: VacationEntityApprover[];

  @IsInt()
  @IsOptional()
  threshold_percent?: number;

  @IsString()
  @IsOptional()
  custom_name?: string;
}

export class CreateVacationScheduleRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  company_ids: string[];

  @IsInt()
  @IsNotEmpty()
  year: number;

  @IsDateString()
  @IsNotEmpty()
  active_to: string;

  @IsBoolean()
  @IsOptional()
  send_to_1c?: boolean;

  @IsString()
  @IsOptional()
  company_comment?: string;

  @IsString()
  @IsOptional()
  custom_name?: string;

  @IsBoolean()
  @IsNotEmpty()
  allow_employee_comment: boolean;

  @IsBoolean()
  @IsNotEmpty()
  add_previous_vacation: boolean;

  @IsBoolean()
  @IsOptional()
  skip_missing_managers?: boolean;

  @ValidateNested()
  @Type(() => VacationValidators)
  @IsNotEmpty()
  validators: VacationValidators;

  @IsOptional()
  validator_vacation_types?: object;

  @IsDateString()
  @IsNotEmpty()
  employee_deadline: string;

  @ValidateNested({ each: true })
  @Type(() => VacationEntityApprover)
  @IsArray()
  @IsOptional()
  approvers?: VacationEntityApprover[];

  @IsIn(['unit', 'all'])
  @IsString()
  @IsOptional()
  teammate_access?: 'unit' | 'all';

  @IsIn(['company', 'units', 'custom_type'])
  @IsString()
  @IsNotEmpty()
  type: 'company' | 'units' | 'custom_type';

  @ValidateNested({ each: true })
  @Type(() => VacationUnitItem)
  @IsArray()
  @IsOptional()
  unit_items?: VacationUnitItem[];

  @IsString()
  @IsOptional()
  custom_type?: string;

  @IsInt()
  @IsOptional()
  threshold_percent?: number;
}

export class GetVacationScheduleAdminListRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];
}

@injectable()
export class VacationsService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async vacationAvailableDays({
    params,
  }: {
    params: VacationAvailableDaysParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, VacationAvailableDaysParams),
    ]);

    let requestPath = '/vacation/available_days/{employee_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof VacationAvailableDaysParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationAvailableDaysResponse,
    );

    return response;
  }

  async vacationScheduleMyDeadline({
    params,
  }: {
    params: VacationScheduleMyDeadlineParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, VacationScheduleMyDeadlineParams),
    ]);

    let requestPath = '/vacation/schedule/{company_id}/my_deadline';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof VacationScheduleMyDeadlineParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationScheduleMyDeadlineResponse,
    );

    return response;
  }

  async vacationScheduleFavorites({
    params,
    body,
  }: {
    params: VacationScheduleFavoritesParams;
    body: VacationFavoritesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, VacationScheduleFavoritesParams),
      this.validator.validateOrReject(body, VacationFavoritesRequest),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/favorites';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof VacationScheduleFavoritesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, body, {});

    return result;
  }

  async vacationScheduleStart({
    params,
    body = {},
  }: {
    params: VacationScheduleStartParams;
    body?: StartVacationScheduleRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, VacationScheduleStartParams),
      this.validator.validateOrReject(body, StartVacationScheduleRequest),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}/start';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof VacationScheduleStartParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async updateVacationSchedule({
    params,
    body = {},
  }: {
    params: UpdateVacationScheduleParams;
    body?: UpdateVacationScheduleRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateVacationScheduleParams),
      this.validator.validateOrReject(body, UpdateVacationScheduleRequest),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateVacationScheduleParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async deleteVacationSchedule({
    params,
  }: {
    params: DeleteVacationScheduleParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteVacationScheduleParams),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeleteVacationScheduleParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, { withSide: true });

    return result;
  }

  async getVacationSchedule({ params }: { params: GetVacationScheduleParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetVacationScheduleParams),
    ]);

    let requestPath = '/vacation/schedule/{schedule_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetVacationScheduleParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationSchedule,
    );

    return response;
  }

  async vacationSchedule({ body }: { body: CreateVacationScheduleRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateVacationScheduleRequest),
    ]);

    let requestPath = '/vacation/schedule';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateVacationScheduleResponse,
    );

    return response;
  }

  async getVacationCustomTypes({
    query,
  }: {
    query: GetVacationCustomTypesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetVacationCustomTypesQuery),
    ]);

    let requestPath = '/vacation/custom_types';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetVacationCustomTypesResponse,
    );

    return response;
  }

  async getVacationScheduleList() {
    let requestPath = '/vacation/schedules';

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationScheduleListResponse,
    );

    return response;
  }

  async searchVacationSchedules({
    query,
  }: {
    query: SearchVacationSchedulesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, SearchVacationSchedulesQuery),
    ]);

    let requestPath = '/vacation/search_schedules';

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      SearchVacationSchedulesResponse,
    );

    return response;
  }

  async getVacationScheduleAdminList({
    body = {},
  }: {
    body?: GetVacationScheduleAdminListRequest;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(
        body,
        GetVacationScheduleAdminListRequest,
      ),
    ]);

    let requestPath = '/vacation/admin_schedules';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationScheduleAdminListResponse,
    );

    return response;
  }

  async getVacationAdminCompaniesList() {
    let requestPath = '/vacation/admin_companies';

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      VacationAdminCompaniesListResponse,
    );

    return response;
  }
}
